<script setup>
import { defineProps, onMounted, ref, watch, defineEmits, computed } from "vue";
import bootstrap from "bootstrap/dist/js/bootstrap";
import axios from "axios";
import Swal from "sweetalert2";
import DataService from "../../services/data.service";
import { useLoading } from "vue-loading-overlay";
import "vue-loading-overlay/dist/css/index.css";
const API_URL = process.env.VUE_APP_API_URL;
const accessToken = localStorage.getItem("access_token");
import { Form, Field, ErrorMessage } from "vee-validate";
import { schemaUpdateUser } from "../schemas/validationUserSchema";
const emit = defineEmits(["empleadoActualizado"]);
import ListaInstituciones from "./ListaInstituciones.vue";
const props = defineProps({
  initialData: Object,
  isEditing: Boolean,
});
const config = {
  headers: {
    Authorization: `Bearer ${accessToken}`,
  },
};
const defaultUser = {
  id: null,
  name: "",
  apellido: "",
  email: "",
  telefono: "",
  instituciones: [],
  licencias: [],
  rol_id: "",
  status: "",
};

const user = ref(
  props.isEditing ? { ...props.initialData } : { ...defaultUser }
);
const Instituciones = ref([]);
const roles = ref([]);
const $loading = useLoading();

const changePassword = ref(false);
const newPassword = ref("");
const newPasswordConfirm = ref("");

const validationSchema = computed(() =>
  schemaUpdateUser({ changePassword: props.isEditing && changePassword.value })
);

const parseInstituciones = (instituciones, institucionesDisponibles) => {
  if (typeof instituciones === "string") {
    const matches = instituciones.match(/\d+/g);
    return matches ? matches.map(Number) : [];
  }

  if (Array.isArray(instituciones)) {
    return instituciones
      .map((num) => Number(num))
      .filter((id) =>
        institucionesDisponibles.some((i) => Number(i.id) === id)
      );
  }

  return [];
};

const saveChanges = async () => {
  const loader = $loading.show({
    color: "#6418a8",
    backgroundColor: "#ffffff",
  });

  try {
    const apiUrl = props.isEditing
      ? API_URL + "usuario/update"
      : API_URL + "usuario/store"; // Cambia la ruta de la API según la acción

    const payload = { ...user.value };
    if (props.isEditing && changePassword.value) {
      payload.password = newPassword.value;
    } else {
      delete payload.password;
    }

    const response = await axios.post(apiUrl, payload, config);
    loader.hide();

    if (response.data.status === true) {
      emit("empleadoActualizado", response.data);
      const modal = bootstrap.Modal.getInstance("#UserModal");
      modal.hide();
    } else {
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: response.data.message,
      });
    }
  } catch (err) {
    loader.hide();
    Swal.fire({
      icon: "error",
      title: "Error",
      text: "Error al guardar el usuario: " + err.message,
    });
  }
};

watch(
  () => props.initialData,
  (newValue) => {
    user.value = newValue;
    changePassword.value = false;
    newPassword.value = "";
    newPasswordConfirm.value = "";
    fetchData();
  }
);

watch(
  () => changePassword.value,
  (enabled) => {
    if (!enabled) {
      newPassword.value = "";
      newPasswordConfirm.value = "";
    }
  }
);

const fetchData = () => {
  if (!user.value.id) return;

  DataService.getUserAccessForId(user.value.id)
    .then((response) => {
      user.value = {
        ...response.data,
        instituciones: parseInstituciones(
          response.data.instituciones,
          Instituciones.value
        ),
        licencias: parseInstituciones(
          response.data.licencias,
          Instituciones.value
        ),
      };
    })
    .catch((error) => {
      console.error("Error obteniendo datos:", error);
    });
};

onMounted(() => {
  DataService.getInstitution()
    .then((response) => {
      Instituciones.value = response.data;
    })
    .catch((error) => {
      console.error("Error obteniendo datos:", error);
    });

  DataService.getRoles()
    .then((response) => {
      roles.value = response.data;
    })
    .catch((error) => {
      console.error("Error obteniendo datos:", error);
    });
});
</script>

<style src="vue-multiselect/dist/vue-multiselect.css"></style>

<style scope>
.alert-error {
  color: red;
}

.alert-success {
  color: green;
}
</style>

<template>
  <div
    class="modal fade"
    id="UserModal"
    tabindex="-1"
    aria-labelledby="UserModalLabel"
    aria-hidden="true"
    ref="modalEle"
  >
    <div class="modal-dialog modal-fullscreen">
      <div class="modal-content">
        <div class="modal-header" style="display: block">
          <div class="row mb-1">
            <div class="col-6">
              <h5 class="modal-title" id="UserModalLabel">
                Datos del empleado
              </h5>
            </div>
          </div>
        </div>
        <div class="modal-body">
          <Form @submit="saveChanges" :validation-schema="validationSchema">
            <div class="row mb-2">
              <div class="col-12 col-md">
                <label for="name" class="form-label">Nombres:</label>
                <Field
                  name="name"
                  type="text"
                  class="form-control"
                  id="name"
                  v-model="user.name"
                />
                <ErrorMessage name="name" />
              </div>
              <div class="col-12 col-md">
                <label for="apellido" class="form-label">Apellidos:</label>
                <Field
                  name="apellido"
                  type="text"
                  class="form-control"
                  id="apellido"
                  v-model="user.apellido"
                />
                <ErrorMessage name="apellido" />
              </div>
            </div>
            <div class="row mb-2">
              <div class="col-12 col-md">
                <label for="email" class="form-label">Email:</label>
                <Field
                  name="email"
                  type="text"
                  class="form-control"
                  id="email"
                  v-model="user.email"
                />
                <ErrorMessage name="email" />
              </div>
              <div class="col-12 col-md">
                <label for="documento" class="form-label">Documento:</label>
                <Field
                  name="documento"
                  type="text"
                  class="form-control"
                  id="documento"
                  v-model="user.documento"
                />
                <ErrorMessage name="documento" />
              </div>
            </div>
            <div class="row mb-2">
              <div class="col-12 col-md">
                <label for="domicilio" class="form-label">Domicilio:</label>
                <Field
                  name="domicilio"
                  type="text"
                  class="form-control"
                  id="domicilio"
                  v-model="user.domicilio"
                />
              </div>
              <div class="col-12 col-md">
                <label for="telefono" class="form-label">Teléfono:</label>
                <Field
                  name="telefono"
                  type="text"
                  class="form-control"
                  id="telefono"
                  v-model="user.telefono"
                />
              </div>
            </div>
            <div class="row mb-2">
              <div class="col-6 col-md">
                <label for="role_id" class="form-label">Rol:</label>
                <Field
                  class="form-control"
                  name="role_id"
                  id="role_id"
                  as="select"
                  v-model="user.role_id"
                >
                  <option v-for="role in roles" :key="role.id" :value="role.id">
                    {{ role.name }}
                  </option>
                </Field>
                <ErrorMessage name="role_id" />
              </div>
              <div class="col-6 col-md">
                <label for="status" class="form-label">Estado:</label>
                <Field
                  class="form-control"
                  name="status"
                  id="status"
                  as="select"
                  v-model="user.status"
                >
                  <option value="Activo">Activo</option>
                  <option value="de_baja">De baja</option>
                </Field>
                <ErrorMessage name="status" />
              </div>
            </div>
            <div class="row mb-2">
              <div class="col-6" v-if="user.role_id == 2 || user.role_id == 3">
                <ListaInstituciones
                  titulo="Seleccione las Instituciones de las cuales el agente es responsable de personal"
                  :instituciones="Instituciones"
                  :seleccionadas="user.instituciones"
                  id="instituciones"
                  name="instituciones"
                  @update:seleccionadas="(val) => (user.instituciones = val)"
                />
                <ErrorMessage name="instituciones" />
              </div>
              <div class="col-6">
                <ListaInstituciones
                  titulo="Seleccione las Instituciones de las cuales el agente es empleado"
                  :instituciones="Instituciones"
                  :seleccionadas="user.licencias"
                  id="licencias"
                  name="licencias"
                  @update:seleccionadas="(val) => (user.licencias = val)"
                />
              </div>
              <ErrorMessage name="licencias" />
            </div>

            <div class="row mb-2" v-if="props.isEditing">
              <div class="col-12">
                <div class="form-check form-switch">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    id="changePasswordSwitch"
                    v-model="changePassword"
                  />
                  <label class="form-check-label" for="changePasswordSwitch">
                    Cambiar contraseña
                  </label>
                </div>
              </div>
            </div>

            <div class="row mb-2" v-if="props.isEditing && changePassword">
              <div class="col-12 col-md">
                <label for="password" class="form-label">Nueva contraseña:</label>
                <Field
                  name="password"
                  type="password"
                  class="form-control"
                  id="password"
                  v-model="newPassword"
                  autocomplete="new-password"
                />
                <ErrorMessage name="password" />
              </div>
              <div class="col-12 col-md">
                <label for="password_confirm" class="form-label">Repetir contraseña:</label>
                <Field
                  name="password_confirm"
                  type="password"
                  class="form-control"
                  id="password_confirm"
                  v-model="newPasswordConfirm"
                  autocomplete="new-password"
                />
                <ErrorMessage name="password_confirm" />
              </div>
            </div>

            <div
              class="modal-footer d-flex flex-column flex-md-row justify-content-center justify-content-md-beetwen text-center w-100"
            >
              <div class="row mb-2 w-100 justify-content-md-end">
                <div class="col-12 col-md-4">
                  <button
                    type="button"
                    class="btn btn-secondary btn-sm w-100"
                    data-bs-dismiss="modal"
                  >
                    Cerrar
                  </button>
                </div>
                <div class="col-12 col-md-4">
                  <button type="submit" class="btn btn-primary btn-sm w-100">
                    Guardar Cambios
                  </button>
                </div>
              </div>
            </div>
          </Form>
        </div>
      </div>
    </div>
  </div>
</template>
